import { type AnnouncementBannerDetailsData } from '@/features/cms/components/CMS/types'
import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { useUtmUrl } from '@/features/shared/hooks/useUtmUrl'
import { routes } from '@shared/constants/routes'
import { Body } from '@shipt/design-system-typography'
import { Button, IconButton } from '@shipt/design-system-buttons'
import { useEffect, useState } from 'react'
import { classNames } from '@/features/shared/utils/classNames'
import { screenSizes } from '@shipt/design-system-themes'
import { NegativeIcon, DuplicateIcon } from '@shipt/design-system-icons'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import {
  useCMSContext,
  useCMSTrackingEvents,
  useDismissCMSContent,
} from '@/features/cms/components/CMS/CMSContext'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { copyToClipboard } from '@/features/shared/utils/copyToClipboard'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `announcement_banner_container` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/announcement_banner_container
 * - **Production** - https://cms.shipt.com/content-type/pool/announcement_banner_container
 *
 */
export const AnnouncementBannerDetails = (props: AnnouncementBannerDetailsData) => {
  const { content_type_id, id } = props
  try {
    return (
      <div
        {...getCMSWrapperProps({
          content_type_id,
          id,
        })}
      >
        <AnnouncementBannerDetailsImpl {...props} />
      </div>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const AnnouncementBannerDetailsImpl = ({
  content_type_id,
  id,
  data: { heading, promo_code, cta_text, color_treatment },
}: AnnouncementBannerDetailsData) => {
  const { trackCMSElementViewed, trackCMSElementClicked } = useCMSTrackingEvents()
  const [promoClicked, setPromoClicked] = useState(false)
  const [closeButtonClicked, setCloseButtonClicked] = useState(false)
  const content = useCMSContext()
  const { dismissContent } = useDismissCMSContent(content)
  const hideBanner = () => {
    // maintain backwards compatibility with existing behavior where dismissible state was not persisted across page views. This component supported dismissibility before CMS supported this functionality, so there are instances of this banner in CMS that do not have cms dismissible rules assigned.
    setCloseButtonClicked(true)
    dismissContent()
  }
  const showPromoCode = Boolean(promo_code?.length)
  const decoratedUrl = useUtmUrl(cta_text?.url || routes.SIGN_UP.url)

  useEffect(() => {
    const trackingMessage = `${heading} ${showPromoCode ? promo_code : cta_text?.text}`.trim()
    trackCMSElementViewed({
      content: trackingMessage,
      type: 'banner',
      message_goal: 'shipt_order_promotion',
    })
  }, [heading, showPromoCode, promo_code, cta_text, trackCMSElementViewed])

  const handleBannerCTAClicked = () => {
    trackCMSElementClicked({ content: cta_text?.text, type: 'banner' })
  }

  const handleCopyPromoCode = () => {
    if (promo_code) {
      setPromoClicked(true)
      copyToClipboard(promo_code)
    }
  }

  return (
    <Banner
      {...getCMSWrapperProps({ content_type_id, id })}
      className={classNames(color_treatment, closeButtonClicked && 'closed')}
    >
      <Heading className="heading" align="center" strong>
        {promoClicked ? 'Code copied! Please apply at checkout.' : heading}
      </Heading>
      {!!showPromoCode && (
        <CopyPromoCodeButton
          leadingIcon={DuplicateIcon}
          onClick={handleCopyPromoCode}
          aria-label={`Copy Promo Code: ${promo_code}`}
          className={color_treatment}
          size="sm"
          variant="tertiary"
          surface={color_treatment === 'primary' ? 'inverse' : 'default'}
        >
          {promo_code}
        </CopyPromoCodeButton>
      )}
      {!showPromoCode && !!cta_text?.text && (
        <CTAButtonLink
          href={decoratedUrl}
          onClick={handleBannerCTAClicked}
          size="sm"
          opensInNewTab={cta_text.open_in_new_tab}
        >
          {cta_text.text}
        </CTAButtonLink>
      )}
      <CloseButtonWrapper>
        <CloseButton
          onClick={hideBanner}
          aria-label="Close Button"
          icon={NegativeIcon}
          className={color_treatment}
          variant="ghost"
          surface={color_treatment === 'primary' ? 'inverse' : 'default'}
        />
      </CloseButtonWrapper>
    </Banner>
  )
}

const Banner = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;
  justify-content: center;
  padding: 12px 35px;
  transition: all 0.25s ease-out;
  will-change: height, padding;

  @media ${screenSizes.tablet} {
    flex-direction: row;
    gap: 0.75rem;
  }

  &.primary {
    background-color: ${theme.color.background.base.brand.primary};

    .heading {
      color: ${theme.color.content.base.neutral.primary_inverse};
    }
  }

  &.secondary {
    background-color: ${theme.color.background.base.brand.secondary};

    .heading {
      color: ${theme.color.content.base.brand.primary};
    }
  }

  &.closed {
    overflow: hidden;
    height: 0;
    padding: 0;
  }
`

const Heading = styled(Body)`
  @media ${screenSizes.smDesktop} {
    font-size: 1.125rem;
  }
`

const CloseButtonWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  display: flex;
  align-items: flex-start;

  @media ${screenSizes.tablet} {
    align-items: center;
  }
`

const CloseButton = styled(IconButton)`
  border: none;
  padding: 0;
  border-radius: 100%;
  width: 2rem;
  height: 2rem;
  margin-top: 0.5rem;
  margin-right: 0.5rem;

  @media ${screenSizes.tablet} {
    margin-top: 0;
    margin-right: 1.5rem;
  }
`

const buttonStyles = css`
  padding: 6px 14px;

  @media ${screenSizes.tablet} {
    padding: 10px 22px;
    font-size: 0.875rem;
  }
`

const CTAButtonLink = styled(ButtonLink)`
  ${buttonStyles};
`

const CopyPromoCodeButton = styled(Button)`
  ${buttonStyles};

  svg {
    width: 1rem;
    height: 1rem;
  }
`
